<script setup lang="ts">
  import MenuItems from './MenuItems.vue';
  import { getCachedOrgInfo } from '~/utils/auth/orgInfoCache';

  const route = useRoute();
  const enterpriseVersion = ref<boolean>(false);

  const checkAuth = async () => {
    try {
      const data = await getCachedOrgInfo();
      enterpriseVersion.value = data?.data?.enterprise_version;
    } catch (err) {
      console.error('Authentication check failed:', err);
    }
  };

  const selectedKeys = computed(() => [
    route.path === '/'
      ? 'dashboard'
      : route.path.startsWith('/donors') || route.path.startsWith('/vendors')
        ? 'contacts'
        : route.path.slice(1)
  ]);

  // Toggle between text logo and image logo
  const showImageLogo = ref(false);
  let intervalId: number | undefined;

  onMounted(() => {
    intervalId = window.setInterval(() => {
      showImageLogo.value = !showImageLogo.value;
    }, 5000); // 10 seconds
    checkAuth();
  });

  onUnmounted(() => {
    if (intervalId) clearInterval(intervalId);
  });
</script>

<template>
  <div>
    <div class="flex items-center pl-6 pr-6 py-0 border-b border-gray-300 shadow-sm fixed top-0 z-[51] bg-white w-full">
      <NuxtLink v-if="enterpriseVersion" href="/" class="bg-blue-900 p-1.5 cursor-pointer">
        <div class="relative">
          <transition name="logo-flip" mode="out-in">
            <!-- TEXT LOGO -->
            <div
              v-if="!showImageLogo"
              class="relative border-2 border-white flex justify-center items-center px-3 py-0.5 overflow-hidden h-[45px] !max-w-[145px] !w-[145px]"
            >
              <div key="text-logo" class="flex justify-center items-center">
                <span class="text-white text-lg font-medium">ERP4</span>
                <span class="text-[#d7dd30] text-lg font-normal">Impact</span>
              </div>
            </div>
            <!-- IMAGE LOGO -->
            <img
              v-else
              key="image-logo"
              src="/assets/images/uwm_logo.png"
              alt="ERP4Impact"
              class="h-[45px] !max-w-[145px] !w-[145px]"
            />
          </transition>
        </div>
      </NuxtLink>
      <NuxtLink v-else href="/" class="bg-blue-900 p-1.5 cursor-pointer">
        <div class="relative border-2 border-white flex justify-center items-center px-3 py-0.5 h-fit overflow-hidden">
          <div key="text-logo" class="flex justify-center items-center">
            <span class="text-white text-lg font-medium">ERP4</span>
            <span class="text-[#d7dd30] text-lg font-normal">Impact</span>
          </div>
        </div>
      </NuxtLink>

      <a-menu
        mode="horizontal"
        class="w-full flex items-center text-zinc-500 font-medium !border-0"
        :selected-keys="selectedKeys"
      >
        <MenuItems />
      </a-menu>
    </div>
    <!-- Spacer div to prevent content from going under navbar -->
    <div class="h-[56px]"></div>
  </div>
</template>

<style scoped>
  .logo-flip-enter-active,
  .logo-flip-leave-active {
    transition:
      opacity 0.9s ease-in-out,
      transform 0.9s ease-in-out;
  }

  /* START STATE */
  .logo-flip-enter-from {
    opacity: 0;
    transform: rotateY(-20deg) scale(1);
  }

  /* END STATE */
  .logo-flip-enter-to {
    opacity: 1;
    transform: rotateY(0deg) scale(1);
  }

  /* LEAVE STATE */
  .logo-flip-leave-from {
    opacity: 1;
    transform: rotateY(0deg) scale(1);
  }

  .logo-flip-leave-to {
    opacity: 0;
    transform: rotateY(20deg) scale(1);
  }
</style>
